import { ScrollArea } from "@components/ui/scroll-area";
import { cn } from "@lib/cn";
import type { TReadingDepthItem } from "@pages/analytics/types";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useMemo } from "react";
import { EmptyStateMessage } from "./empty-state-message";
import { ErrorStateMessage } from "./error-state-message";

// API response format
interface SessionReadingDepthData {
  event: string;
  duration: number;
  displayedTime: string;
  slide_index: number | null;
  slides_count: number | null;
}

interface ReadingDepthFunnelProps {
  items?: TReadingDepthItem[] | SessionReadingDepthData[];
  isLoading: boolean;
  isError: boolean;
}

const ReadingDepthRow = ({
  item,
  isMissing,
}: {
  item: TReadingDepthItem;
  isMissing?: boolean;
}) => {
  return (
    <div
      className={cn(
        "tw-flex tw-items-center tw-gap-6 tw-border-b tw-border-blue-100 tw-py-3 tw-px-4 tw-border-solid tw-min-w-0 -lg:tw-gap-4 -lg:tw-px-2",
        isMissing && "tw-opacity-50",
      )}
    >
      <span className="tw-font-sans tw-text-sm tw-text-blue-800 tw-w-[22px] tw-flex-shrink-0">
        {item.number}.
      </span>
      <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0 tw-overflow-hidden">
        {!isMissing ? (
          <IconCheck
            className="tw-size-4 tw-text-green-500 tw-flex-shrink-0"
            stroke={2}
          />
        ) : (
          <IconX
            className="tw-size-4 tw-text-red-500 tw-flex-shrink-0"
            stroke={2}
          />
        )}
        <span className="tw-font-sans tw-text-sm tw-text-blue-800 tw-font-medium tw-truncate tw-min-w-0">
          {item.slide}
        </span>
      </div>
      <span className="tw-font-sans tw-text-sm tw-text-blue-800 tw-text-right tw-w-[83px] tw-flex-shrink-0">
        {item.time}
      </span>
    </div>
  );
};

const Container = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="tw-rounded-lg tw-outline tw-outline-1 tw-py-5 tw-outline-blue/5 tw-bg-white tw-relative">
      <span className="tw-pb-4 tw-text-sm tw-font-sans tw-font-semibold tw-text-blue tw-block tw-px-5">
        Reading depth funnel
      </span>
      {children}
    </div>
  );
};

const TableContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <ScrollArea className="tw-px-5 tw-h-[370px]">
      <div className="tw-min-w-0 tw-w-full">
        <div className="tw-flex tw-items-center tw-gap-6 tw-border-b tw-border-blue-100 tw-py-3 tw-px-4 tw-border-solid -lg:tw-gap-4 -lg:tw-px-2">
          <span className="tw-font-sans tw-text-sm tw-text-blue-800 tw-w-[22px] tw-flex-shrink-0">
            No.
          </span>
          <span className="tw-font-sans tw-flex-1 tw-text-sm tw-text-blue-800 tw-min-w-0">
            Slide name
          </span>
          <span className="tw-font-sans tw-text-sm tw-text-blue-800 tw-text-right tw-w-[83px] tw-flex-shrink-0">
            Time on slide
          </span>
        </div>
        {children}
      </div>
    </ScrollArea>
  );
};

export const ReadingDepthFunnel = ({
  items,
  isLoading,
  isError,
}: ReadingDepthFunnelProps) => {
  // Transform API data to component format if needed
  const transformedItems = useMemo(() => {
    if (!items?.length) return [];

    // Check if items are in API format (SessionReadingDepthData)
    const firstItem = items[0];
    if ("event" in firstItem && "displayedTime" in firstItem) {
      // Transform from API format to ReadingDepthItem
      return (items as SessionReadingDepthData[]).map((item, index) => ({
        number: (item.slide_index ?? index) + 1,
        slide: item.event,
        time: item.displayedTime,
        isViewed: item.duration > 0,
        slide_index: item.slide_index,
        slides_count: item.slides_count,
      }));
    }

    // Already in ReadingDepthItem format
    return items as TReadingDepthItem[];
  }, [items]);

  const hasItems = transformedItems && transformedItems.length > 0;

  // Optimized missing slides calculation
  const missingSlides = useMemo(() => {
    if (!transformedItems?.length) return [];

    const firstItem = transformedItems[0];
    const lastItem = transformedItems[transformedItems.length - 1];

    // Type-safe checks
    if (
      typeof firstItem.slides_count !== "number" ||
      typeof lastItem.slide_index !== "number"
    ) {
      return [];
    }

    const slidesCount = firstItem.slides_count;
    const lastSlideIndex = lastItem.slide_index;

    // Early return if no missing slides
    if (lastSlideIndex >= slidesCount - 1) return [];

    // Calculate how many slides we can actually add
    const availableSlots = Math.max(0, slidesCount - transformedItems.length);
    const missingCount = Math.min(
      slidesCount - 1 - lastSlideIndex,
      availableSlots,
    );

    // Generate missing slides efficiently
    return Array.from({ length: missingCount }, (_, i) => ({
      number: lastSlideIndex + i + 2,
      slide: `Slide #${lastSlideIndex + i + 2}`,
      time: "00:00",
      isViewed: false,
      slide_index: lastSlideIndex + i + 1,
      slides_count: slidesCount,
    }));
  }, [transformedItems]);

  if (isError) {
    return (
      <Container>
        <ErrorStateMessage />
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container>
        <TableContainer>
          <ReadingDepthFunnelSkeleton />
        </TableContainer>
      </Container>
    );
  }

  if (!hasItems) {
    return (
      <Container>
        <EmptyStateMessage
          title="No reading data available"
          description="Reading depth information will appear here once the session is complete."
          className="tw-max-w-72 tw-mx-auto tw-h-[370px]"
        />
      </Container>
    );
  }

  return (
    <Container>
      <TableContainer>
        {transformedItems.map((item) => (
          <ReadingDepthRow key={item.number} item={item} />
        ))}
        {missingSlides.map((item) => (
          <ReadingDepthRow
            key={`missing-${item.slide_index}-${item.number}`}
            item={item}
            isMissing
          />
        ))}
      </TableContainer>
    </Container>
  );
};

const ReadingDepthFunnelSkeleton = () => {
  return (
    <div className="tw-px-5 tw-h-[370px]">
      {Array.from({ length: 7 }).map((_, index) => (
        <div
          key={index}
          className="tw-animate-skeleton-loading tw-flex tw-items-center tw-gap-6 tw-border-b tw-border-blue-100 tw-p-4 tw-border-solid -lg:tw-gap-4 -lg:tw-px-2"
        >
          <div className="tw-flex tw-items-center tw-gap-6 tw-flex-1 tw-min-w-0 tw-overflow-hidden">
            <span className="tw-rounded tw-bg-blue-100/50 tw-block tw-h-1.5 tw-w-6" />
            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-size-4 tw-rounded tw-bg-blue-100/50 tw-block" />
              <span className="tw-h-1.5 tw-w-32 tw-rounded-lg tw-bg-blue-100/50 tw-block" />
            </div>
            <span className="tw-h-1.5 tw-w-10 tw-ml-auto tw-rounded-lg tw-bg-blue-100/50 tw-block" />
          </div>
        </div>
      ))}
    </div>
  );
};
